import { Rng } from '../game/Rng.ts';
import { BaseInputDevice } from './InputDevice.ts';

export interface SimulatedEmgOptions {
  /** Sample rate, Hz. */
  sampleRateHz: number;
  /** Resting signal level, device units. */
  baseline: number;
  /** Std dev of resting noise, device units. */
  noiseStdDev: number;
  /** Peak amplitude added during a contraction. */
  burstAmplitude: number;
  /** Duration of a contraction burst, ms. */
  burstDurationMs: number;
  /** Rise/fall shaping of the burst envelope, ms. */
  burstRiseMs: number;
  seed: number;
}

export const DEFAULT_SIMULATED_EMG: SimulatedEmgOptions = {
  sampleRateHz: 500,
  baseline: 40,
  noiseStdDev: 8,
  burstAmplitude: 300,
  burstDurationMs: 140,
  burstRiseMs: 25,
  seed: 4242,
};

/**
 * Synthetic EMG source for developing `EmgController` with no electrodes.
 *
 * Emits a noisy baseline, plus a shaped burst whenever `burst()` is called.
 * `SimulatedHumanStimulator` calls it at the same instant it presses the
 * virtual button, so the simulated participant produces a muscle signal and a
 * switch closure from one simulated movement -- which is what the real setup
 * does, and what lets EMG-triggered and switch-triggered control be compared
 * against the same underlying response model.
 *
 * Deterministic: the noise comes from a seeded `Rng`, and samples are generated
 * from an explicit clock passed to `pump()` rather than from a timer.
 */
export class SimulatedEmgDevice extends BaseInputDevice {
  readonly id = 'simulated-emg';
  readonly label = 'Simulated EMG (no hardware)';
  override readonly providesEmg = true;

  readonly options: SimulatedEmgOptions;

  private rng: Rng;
  private nextSampleAt = 0;
  private bursts: { start: number; end: number }[] = [];
  private sampleCount = 0;
  private lastValue = 0;

  constructor(options: Partial<SimulatedEmgOptions> = {}) {
    super();
    this.options = { ...DEFAULT_SIMULATED_EMG, ...options };
    this.rng = new Rng(this.options.seed);
  }

  async connect(): Promise<void> {
    this.status = 'connected';
    this.nextSampleAt = 0;
  }

  async disconnect(): Promise<void> {
    this.status = 'disconnected';
    this.bursts = [];
  }

  reset(seed = this.options.seed): void {
    this.rng = new Rng(seed);
    this.bursts = [];
    this.nextSampleAt = 0;
    this.sampleCount = 0;
  }

  /** Schedule a contraction starting at `timestamp`. */
  burst(timestamp: number, durationMs = this.options.burstDurationMs): void {
    this.bursts.push({ start: timestamp, end: timestamp + durationMs });
  }

  /**
   * Generate every sample due up to `nowMs`.
   *
   * Called once per animation frame from the app loop. Catching up in a loop
   * rather than emitting one sample per frame keeps the effective sample rate
   * independent of frame rate, which matters because the EMG threshold detector
   * downstream has its own time constants.
   */
  pump(nowMs: number): void {
    if (this.status !== 'connected') return;
    const period = 1000 / this.options.sampleRateHz;
    if (this.nextSampleAt === 0) this.nextSampleAt = nowMs;
    // Bound the catch-up so a backgrounded tab does not emit a million samples.
    let budget = Math.ceil(this.options.sampleRateHz * 0.5);
    while (this.nextSampleAt <= nowMs && budget-- > 0) {
      this.emitEmg({
        value: this.sampleAt(this.nextSampleAt),
        timestamp: this.nextSampleAt,
        channel: 0,
        source: this.id,
      });
      this.sampleCount++;
      this.nextSampleAt += period;
    }
    if (budget <= 0) this.nextSampleAt = nowMs + period;
    this.bursts = this.bursts.filter((b) => b.end > nowMs - 1000);
  }

  private sampleAt(t: number): number {
    const o = this.options;
    let value = o.baseline + this.rng.normal(0, o.noiseStdDev);
    for (const b of this.bursts) {
      if (t < b.start || t > b.end) continue;
      const since = t - b.start;
      const until = b.end - t;
      const envelope =
        Math.min(1, since / Math.max(1, o.burstRiseMs)) *
        Math.min(1, until / Math.max(1, o.burstRiseMs));
      // Rectified interference pattern: envelope times |noise|, not a smooth bump.
      value += envelope * o.burstAmplitude * Math.abs(this.rng.normal(0.7, 0.25));
    }
    this.lastValue = value;
    return value;
  }

  override info(): Record<string, unknown> {
    return {
      ...super.info(),
      sampleRateHz: this.options.sampleRateHz,
      samples: this.sampleCount,
      lastValue: Math.round(this.lastValue),
      activeBursts: this.bursts.length,
    };
  }
}
